import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { usePartnerRecord, isPartnerUsable } from "@/hooks/usePartner";
import AppErrorBoundary from "@/components/AppErrorBoundary";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

/**
 * Gates /partner/* by the lifecycle of the signed-in user's partners row:
 *
 *   no row                                → /partner/onboarding (apply)
 *   pending_review | more_info_requested  → /partner/pending
 *   active (and not expired)              → the requested page
 *   rejected | suspended | expired        → /partner/pending (shows why)
 *
 * This is UX routing only. Every partner read/write is independently enforced by
 * the is_partner_of() / is_active_partner() RLS policies, which also embed the
 * valid_until expiry — so a stale client here still cannot touch client data.
 */
const PartnerRoute = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const { data: partner, isLoading: partnerLoading } = usePartnerRecord();

  if (loading) return <Spinner />;
  // Signed out: the partner sign-up page (it links to sign-in for existing accounts).
  if (!user) return <Navigate to="/partner/register" replace />;
  if (partnerLoading) return <Spinner />;

  const path = location.pathname;
  const onOnboarding = path.startsWith("/partner/onboarding");
  const onPending = path.startsWith("/partner/pending");

  // Never applied, or still drafting the application → the onboarding form.
  if (!partner || partner.status === "draft") {
    return onOnboarding ? <Outlet /> : <Navigate to="/partner/onboarding" replace />;
  }

  // "more_info_requested" reopens the form, so let them reach it from /pending.
  if (partner.status === "more_info_requested" && onOnboarding) {
    return (
      <AppErrorBoundary context="partner" resetKey={path}>
        <Outlet />
      </AppErrorBoundary>
    );
  }

  if (!isPartnerUsable(partner)) {
    return onPending ? <Outlet /> : <Navigate to="/partner/pending" replace />;
  }

  // Active partner: the onboarding and pending screens no longer apply.
  if (onOnboarding || onPending) return <Navigate to="/partner/dashboard" replace />;

  return (
    <AppErrorBoundary context="partner" resetKey={path + location.search}>
      <Outlet />
    </AppErrorBoundary>
  );
};

export default PartnerRoute;
